












































































































































































































































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import RequestPromises from "@/mixins/requestPromises";
import { DataModules } from "@/store/modules/data";
import { mapGetters, mapState } from "vuex";
import { IState } from "@/store";
import { ITaxTag, ITaxTagLocation } from "@/models/tax";
import {
  TaxTagTypes,
  TaxTagTypesMap,
  TaxTagBaseTypes,
  TaxTagBaseTypesMap
} from "@/data/enums/tax";
import HasValidation from "@/mixins/HasValidation";
import AdminBrandId from "@/mixins/adminBrandId";
import { ICurrency } from "@/models/constants";
import TaxLocationsTableView from "@/components/app/global/tax/locations/taxLocationsTableView.vue";
import CurrenciesStore from "@/mixins/constants/currenciesStore";
import { TranslationsTypes } from "@/data/enums/translations";
import objectHash from "object-hash";

const initialForm = {
  id: "",
  name: "",
  type: TaxTagTypes.PERCENT,
  amount: 0,
  company_type: TaxTagTypes.PERCENT,
  company_amount: 0,
  show_on_item: false,
  show_on_invoice: false,
  base_type: TaxTagBaseTypes.NET,
  locations: [] as ITaxTagLocation[],
  require_company_reg_number: false,
  require_company_vat_number: false
} as Partial<ITaxTag>;

@Component({
  components: {
    TaxLocationsTableView,
    "location-select": () =>
      import("@/components/app/global/tax/locations/locationSelect.vue")
  },
  computed: {
    ...mapGetters({
      brandCurrencies: "brand/supportedCurrencies"
    }),
    ...mapState({
      taxTag(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.TAX_TAGS
        });
      }
    })
  }
})
export default class AddEditTaxTagFormModal extends Mixins(
  RequestPromises,
  HasValidation,
  CurrenciesStore,
  AdminBrandId
) {
  // Props
  @Prop({ default: 0 })
  public taxTagId!: number;

  // Data
  public applySecondaryRate = false;
  public brandCurrencies!: ICurrency[];
  public form: Partial<ITaxTag> = {};
  public isLoading = true;
  public isProcessing = false;
  public isReloading = false;
  public localTaxTagId = this.taxTagId;
  public locations: ITaxTagLocation[] = [];
  public locationsFromDB: ITaxTagLocation[] = [];
  public requestError = false;
  public taxTag!: ITaxTag;
  public taxTagBaseTypesMap = TaxTagBaseTypesMap();
  public taxTagTypesMap = TaxTagTypesMap();

  // Lifecycle methods
  public async created() {
    if (!this.contentIsAvailable) return;
    await this.loadData();
    this.checkForSecondaryRate();
  }

  public beforeDestroy() {
    this.$store.dispatch(`data/binSingle`, {
      storeModule: DataModules.TAX_TAGS,
      vm: this
    });
  }

  // Computed
  public get isFormDisabled() {
    return this.isSystemTag || !this.canEdit || this.isProcessing;
  }

  public get guardFunctionalities() {
    return ["get_taxtag", "list_taxtags_locations"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  public get canEdit() {
    return this.$userCan("update_taxtag", "sync_taxtags_locations");
  }

  public get canCreate() {
    return this.$userCan("create_taxtag");
  }

  public get isSystemTag() {
    return this.$_.get(this.taxTag, "system_tag");
  }

  public get isEdit() {
    return this.localTaxTagId;
  }

  public get isCompanyTypePercentage() {
    return this.form.company_type === TaxTagTypes.PERCENT;
  }

  public get isCompanyTypeFixedAmount() {
    return this.form.company_type === TaxTagTypes.FIXED;
  }

  public get isFixedAmount() {
    return (
      this.form.type === TaxTagTypes.FIXED ||
      this.form.company_type === TaxTagTypes.FIXED
    );
  }

  public get hasChange() {
    const left = this.sanitizeForm(this.form || {});
    const right = this.sanitizeForm(this.taxTag || {});
    return !(
      this.$_.isEqual(left, right) &&
      this.$_.isEqual(this.locations, this.locationsFromDB)
    );
  }

  public get i18n() {
    return {
      addLocation: this.$t("_action.add_object", {
        object: this.$t("_.location")
          .toString()
          .toLowerCase()
      }),
      modalTitle: this.localTaxTagId
        ? this.$t("_action.edit_object", {
            object: this.$t("_.tax_tag")
              .toString()
              .toLowerCase()
          })
        : this.$t("_action.add_object", {
            object: this.$t("_.tax_tag")
              .toString()
              .toLowerCase()
          })
    };
  }

  public get supportedCurrencies() {
    if (
      this.$_.some(
        this.brandCurrencies,
        currency => currency.id === this.form.currency_id
      )
    ) {
      return this.brandCurrencies;
    }
    const currency = this.$_.find(
      this.currencies,
      currency => currency.id === this.form.currency_id
    );

    if (!currency) return this.brandCurrencies;

    return this.$_.orderBy(
      this.$_.concat(this.brandCurrencies, [currency]),
      "name",
      "asc"
    );
  }

  // Methods

  public checkForSecondaryRate() {
    const { company_amount, amount, company_type, type } = this.form;
    this.applySecondaryRate = !(
      company_type === type && company_amount === amount
    );
  }

  public toggleSecondaryRate() {
    this.applySecondaryRate = !this.applySecondaryRate;
    if (!this.applySecondaryRate) {
      this.form.company_amount = this.form.amount;
      this.form.company_type = this.form.type;
      this.form.require_company_reg_number = false;
      this.form.require_company_vat_number = false;
    }
  }

  public isPercentage(type) {
    return type === TaxTagTypes.PERCENT;
  }

  public sanitizeForm(data) {
    return {
      ...this.$_.pick(data, [
        "name",
        "type",
        "amount",
        "company_type",
        "company_amount",
        "show_on_item",
        "show_on_invoice",
        "base_type",
        "brand_id",
        "require_company_reg_number",
        "require_company_vat_number",
        this.isFixedAmount ? "currency_id" : ""
      ]),
      show_on_item: data.show_on_item ? 1 : 0,
      show_on_invoice: data.show_on_invoice ? 1 : 0
    };
  }

  public async loadData() {
    try {
      await this.fetchTaxTag();
      await this.getLocations();
      this.setForm();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public fetchTaxTag() {
    if (!this.localTaxTagId) return Promise.resolve();
    return this.$store.dispatch(`data/${DataModules.TAX_TAGS}/get`, {
      id: this.localTaxTagId,
      vm: this
    });
  }

  public async getLocations() {
    if (!this.localTaxTagId) return Promise.resolve();

    this.locations = await this.$store.dispatch(
      `data/${DataModules.TAX_LOCATIONS}/list`,
      {
        taxTagId: this.localTaxTagId,
        storeData: false,
        vm: this,
        params: {
          limit: 0,
          with: "country,region"
        }
      }
    );
    this.locationsFromDB = this.$_.cloneDeep(this.locations);
  }

  public async reloadData() {
    this.isReloading = true;
    this.fetchTaxTag();
    this.isReloading = false;
  }

  public setForm() {
    if (!this.localTaxTagId)
      this.form = { ...initialForm, brand_id: this.brandId };
    else this.form = this.$_.cloneDeep(this.taxTag);

    this.form.show_on_invoice = Boolean(this.form.show_on_invoice);
    this.form.show_on_item = Boolean(this.form.show_on_item);
  }

  public async openTranslateModal(fieldName: string) {
    if (this.isFormDisabled || !this.isEdit) return;
    this.$store.dispatch(
      `data/${DataModules.TRANSLATIONS}/openTranslationsModal`,
      {
        props: {
          objectId: this.localTaxTagId,
          objectTypeCode: TranslationsTypes.TAX_TAG,
          brandId: this.taxTag.brand_id,
          fieldType: "text",
          fieldLabel: this.$t("_.name"),
          fieldName
        }
      }
    );
  }

  public createTaxTag() {
    return this.$store.dispatch(`data/${DataModules.TAX_TAGS}/create`, {
      data: this.sanitizeForm(this.form)
    });
  }

  public updateTaxTag() {
    return this.$store.dispatch(`data/${DataModules.TAX_TAGS}/update`, {
      id: this.localTaxTagId,
      data: this.sanitizeForm(this.form)
    });
  }

  public updateLocations() {
    const sanitizedLocations = this.locations.map(i =>
      this.$_.pick(i, [
        i.region_id ? "region_id" : "",
        "country_id",
        "post_code"
      ])
    );

    return this.$store.dispatch(`data/${DataModules.TAX_LOCATIONS}/update`, {
      taxTagId: this.localTaxTagId,
      data: { locations: sanitizedLocations }
    });
  }

  public removeLocation({ hash }) {
    if (!this.canEdit) return;
    this.locations = this.locations.filter(i => objectHash.sha1(i) !== hash);
  }

  public addTaxTagLocation(taxLocation: ITaxTagLocation) {
    const hash = objectHash.sha1(
      this.$_.pick(taxLocation, ["country_id", "region_id", "post_code"])
    );
    const found = this.locations.find(
      i =>
        objectHash.sha1(
          this.$_.pick(i, ["country_id", "region_id", "post_code"])
        ) === hash
    );

    if (found) return;
    this.locations.push(taxLocation);
  }

  public openAddEditLocationModal() {
    this.$store.dispatch(`data/${DataModules.TAX_LOCATIONS}/openAddEditModal`, {
      props: {
        taxLocations: []
      },
      events: {
        save: ({ taxLocations }) => {
          this.$_.forEach(taxLocations, taxLocation =>
            this.locations.push(taxLocation)
          );
        }
      }
    });
  }

  public async validateBeforeSubmit() {
    if (this.isSystemTag || !this.canEdit) return;
    try {
      this.isProcessing = true;
      const result = await this.validate();
      if (result) {
        let taxTag: ITaxTag | null = null;

        if (this.localTaxTagId) {
          // If edit
          taxTag = await this.updateTaxTag();
        } else {
          // else create
          const taxTag = await this.createTaxTag();
          this.localTaxTagId = taxTag.id;
        }

        await this.updateLocations();
        this.$emit("success", taxTag);
        this.$emit("close");
      } else {
        throw this.$t("_sentence.form_is_not_valid");
      }
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }
}
